import { Box, Stack, Typography } from '@mui/material';
import React from 'react';

import { colors } from '../../components';
import { EmptyStateWithLoader } from '../../components/empty-state';
import CommonHeader from '../../components/ui-components/CommonHeader';
import { AssetApiResponse } from '../../services/assets-service';
import { useTranslation } from '../../utils/translations';

interface AssetsTabProps {
    contractId?: string;
    data?: AssetApiResponse[];
    loading?: boolean;
}

const AssetsTab: React.FC<AssetsTabProps> = ({ data = [], loading = false }) => {
    const { t } = useTranslation();

    // Helper function to format location
    const formatLocation = (asset: AssetApiResponse): string => {
        const parts = [asset.Address, asset.City, asset.State, asset.PostalCode, asset.Country].filter(Boolean);
        return parts.join(', ') || 'N/A';
    };

    if (loading) {
        return <EmptyStateWithLoader loading empty={false} />;
    }

    return (
        <Stack
            bgcolor={colors.fullWhite}
            borderRadius={3}
            p={2}
            gap={2}
            m={3}
            mt={0}
            sx={{ border: `1px solid ${colors.greyPreset[20]}` }}
        >
            {data.map((asset, index) => (
                <Box key={asset.Id || index}>
                    <Stack
                        bgcolor={colors.primaryLight[5]}
                        p={2}
                        borderRadius={2}
                        gap={2.25}
                        sx={{ border: `1px solid ${colors.greyPreset[20]}` }}
                    >
                        <Stack direction={'row'} gap={2} justifyContent={'space-between'}>
                            <CommonHeader
                                mainTitle={asset.AssetDesc || 'N/A'}
                                mainTitleFS={14}
                                subTitleFS={14}
                                subTitle={
                                    <>
                                        {asset.AssetName || 'N/A'}{' '}
                                        {/* <Typography variant="body2" component="span" color="textSecondary" ml={3}>
                                            {asset.CreatedTime ? formatToUSDate(asset.CreatedTime) : 'N/A'}
                                        </Typography> */}
                                    </>
                                }
                                icon="icon-vehicle"
                                iconSize={20}
                                iconBgColor={colors.primaryLight[100]}
                                iconColor={colors.fullWhite}
                                iconPadding={1.5}
                                cardGap={1.5}
                            />
                        </Stack>
                        {/* Details Grid */}
                        <Box
                            display="grid"
                            rowGap={2.5}
                            sx={{
                                gridTemplateColumns: { xs: 'repeat(3, 1fr)', md: 'repeat(5, auto)' },
                                columnGap: { xs: 2, md: 4 },
                                bgcolor: colors.fullWhite,
                                mx: -2,
                                mb: -2,
                                px: 2,
                                py: 2.25,
                                borderBottomLeftRadius: theme => theme.shape.borderRadius * 2,
                                borderBottomRightRadius: theme => theme.shape.borderRadius * 2,
                            }}
                        >
                            <Stack>
                                <Typography variant="caption" color="textSecondary" mb={0.5}>
                                    {t('contracts.details.SerialNumber')}
                                </Typography>
                                <Typography variant="subtitle2" fontWeight={700} color={'textPrimary'}>
                                    {asset.SerialNumber || 'N/A'}
                                </Typography>
                            </Stack>
                            <Stack>
                                <Typography variant="caption" color="textSecondary" mb={0.5}>
                                    {t('contracts.details.Make')}
                                </Typography>
                                <Typography variant="subtitle2" fontWeight={700} color={'textPrimary'}>
                                    {asset.Make || 'N/A'}
                                </Typography>
                            </Stack>
                            <Stack>
                                <Typography variant="caption" color="textSecondary" mb={0.5}>
                                    {t('contracts.details.Model')}
                                </Typography>
                                <Typography variant="subtitle2" fontWeight={700} color={'textPrimary'}>
                                    {asset.Model || 'N/A'}
                                </Typography>
                            </Stack>
                            <Stack>
                                <Typography variant="caption" color="textSecondary" mb={0.5}>
                                    {t('contracts.details.Year')}
                                </Typography>
                                <Typography variant="subtitle2" fontWeight={700} color={'textPrimary'}>
                                    {asset.Year || 'N/A'}
                                </Typography>
                            </Stack>
                            <Stack sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
                                <Typography variant="caption" color="textSecondary" mb={0.5}>
                                    {t('contracts.details.Location')}
                                </Typography>
                                <Typography variant="subtitle2" fontWeight={700} color={'textPrimary'}>
                                    {formatLocation(asset)}
                                </Typography>
                            </Stack>
                        </Box>
                    </Stack>
                </Box>
            ))}
        </Stack>
    );
};

export default AssetsTab;
